import { useEffect, useRef } from "react";
import {
  ColorType,
  CrosshairMode,
  createChart,
  type IChartApi,
  type ISeriesApi,
  type SeriesMarker,
  type Time,
  type UTCTimestamp,
} from "lightweight-charts";
import type { ChartSnapshot } from "../types";

type Props = {
  snapshot: ChartSnapshot;
  showSma: boolean;
  showRsi: boolean;
  showMarkers: boolean;
  showLevels: boolean;
  fitKey: number;
  mode?: "candles" | "line";
  visibleWindowSeconds?: number;
  height?: number;
};

export const PriceChart = ({
  snapshot,
  showSma,
  showRsi,
  showMarkers,
  showLevels,
  fitKey,
  mode = "candles",
  visibleWindowSeconds,
  height,
}: Props) => {
  const priceRef = useRef<HTMLDivElement | null>(null);
  const rsiRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<{ price: IChartApi; rsi: IChartApi | null } | null>(
    null,
  );

  useEffect(() => {
    const priceHost = priceRef.current;
    if (!priceHost || snapshot.bars.length === 0) return;

    const chart = createChart(priceHost, {
      autoSize: true,
      height: height ?? (showRsi ? 430 : 520),
      layout: {
        background: { type: ColorType.Solid, color: "#11151b" },
        textColor: "#aab1bd",
        fontFamily:
          "Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
      },
      grid: {
        vertLines: { color: "rgba(124, 132, 143, 0.18)" },
        horzLines: { color: "rgba(124, 132, 143, 0.18)" },
      },
      crosshair: { mode: CrosshairMode.Normal },
      rightPriceScale: { borderColor: "rgba(124, 132, 143, 0.28)" },
      timeScale: {
        borderColor: "rgba(124, 132, 143, 0.28)",
        timeVisible: mode === "line",
        secondsVisible: mode === "line",
        rightOffset: mode === "line" ? 6 : 0,
      },
    });
    const priceSeries =
      mode === "line"
        ? chart.addAreaSeries({
            lineColor: "#00a98f",
            topColor: "rgba(0, 169, 143, 0.24)",
            bottomColor: "rgba(0, 169, 143, 0.02)",
            lineWidth: 2,
            priceLineVisible: true,
            lastValueVisible: true,
          })
        : chart.addCandlestickSeries({
            upColor: "#00a98f",
            downColor: "#ff4d57",
            wickUpColor: "#00a98f",
            wickDownColor: "#ff4d57",
            borderVisible: false,
          });

    if (mode === "line") {
      priceSeries.setData(
        snapshot.bars.map((bar) => ({
          time: toChartTime(bar.time),
          value: bar.close,
        })),
      );
    } else {
      priceSeries.setData(
        snapshot.bars.map((bar) => ({
          time: toChartTime(bar.time),
          open: bar.open,
          high: bar.high,
          low: bar.low,
          close: bar.close,
        })),
      );
    }

    if (showSma) {
      addLine(chart, snapshot.overlays.sma20, "#ffd21f", "SMA20");
      addLine(chart, snapshot.overlays.sma50, "#2d6bff", "SMA50");
      addLine(chart, snapshot.overlays.sma200, "#8b5cf6", "SMA200");
    }

    if (showLevels) {
      for (const level of snapshot.levels) {
        priceSeries.createPriceLine({
          price: level.price,
          color:
            level.kind === "stop"
              ? "#ff4d57"
              : level.kind === "target"
                ? "#3fb36b"
                : "#2d6bff",
          lineWidth: 1,
          lineStyle: level.kind === "entry" ? 0 : 2,
          axisLabelVisible: true,
          title: level.kind.toUpperCase(),
        });
      }
    }
    if (mode === "line") {
      addScaleBounds(chart, snapshot);
    }

    if (showMarkers) {
      const markers: SeriesMarker<Time>[] = snapshot.markers.map((m) => ({
        time: toChartTime(m.time),
        position: m.position,
        color: m.color,
        shape: m.shape,
        text: m.text,
      }));
      priceSeries.setMarkers(markers);
    }

    if (mode === "line" && visibleWindowSeconds) {
      const lastTime = toTimestamp(snapshot.bars.at(-1)?.time);
      if (lastTime !== null) {
        chart.timeScale().setVisibleRange({
          from: (lastTime - visibleWindowSeconds) as UTCTimestamp,
          to: lastTime as UTCTimestamp,
        });
      } else {
        chart.timeScale().fitContent();
      }
    } else {
      chart.timeScale().fitContent();
    }
    let rsiChart: IChartApi | null = null;
    if (showRsi && rsiRef.current && snapshot.overlays.rsi14.length > 0) {
      rsiChart = createChart(rsiRef.current, {
        autoSize: true,
        height: 150,
        layout: {
          background: { type: ColorType.Solid, color: "#151323" },
          textColor: "#aab1bd",
          fontFamily:
            "Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
        },
        grid: {
          vertLines: { color: "rgba(124, 132, 143, 0.16)" },
          horzLines: { color: "rgba(124, 132, 143, 0.16)" },
        },
        rightPriceScale: {
          borderColor: "rgba(124, 132, 143, 0.28)",
          autoScale: false,
          scaleMargins: { top: 0.08, bottom: 0.08 },
        },
        timeScale: {
          borderColor: "rgba(124, 132, 143, 0.28)",
          timeVisible: false,
        },
      });
      const rsiLine = rsiChart.addLineSeries({
        color: "#8b5cf6",
        lineWidth: 1,
        title: "RSI14",
        priceLineVisible: false,
      });
      rsiLine.setData(
        snapshot.overlays.rsi14.map((p) => ({ time: p.time, value: p.value })),
      );
      rsiLine.createPriceLine({
        price: 70,
        color: "rgba(170, 177, 189, 0.55)",
        lineWidth: 1,
        lineStyle: 2,
        axisLabelVisible: true,
        title: "70",
      });
      rsiLine.createPriceLine({
        price: 30,
        color: "rgba(170, 177, 189, 0.55)",
        lineWidth: 1,
        lineStyle: 2,
        axisLabelVisible: true,
        title: "30",
      });
      rsiChart.timeScale().fitContent();
      chart.timeScale().subscribeVisibleLogicalRangeChange((range) => {
        if (range !== null) rsiChart?.timeScale().setVisibleLogicalRange(range);
      });
    }
    chartRef.current = { price: chart, rsi: rsiChart };

    return () => {
      chart.remove();
      rsiChart?.remove();
      chartRef.current = null;
    };
  }, [
    snapshot,
    showSma,
    showRsi,
    showMarkers,
    showLevels,
    mode,
    visibleWindowSeconds,
    height,
  ]);

  useEffect(() => {
    chartRef.current?.price.timeScale().fitContent();
    chartRef.current?.rsi?.timeScale().fitContent();
  }, [fitKey]);

  return (
    <div className="tp-chart-stack">
      <div ref={priceRef} className="tp-price-chart" />
      {showRsi && <div ref={rsiRef} className="tp-rsi-chart" />}
    </div>
  );
};

function addLine(
  chart: IChartApi,
  points: ChartSnapshot["overlays"]["sma20"],
  color: string,
  title: string,
): ISeriesApi<"Line"> {
  const line = chart.addLineSeries({
    color,
    lineWidth: 1,
    title,
    priceLineVisible: false,
    lastValueVisible: false,
  });
  line.setData(points.map((p) => ({ time: toChartTime(p.time), value: p.value })));
  return line;
}

function addScaleBounds(chart: IChartApi, snapshot: ChartSnapshot): void {
  const times = snapshot.bars.map((bar) => toChartTime(bar.time));
  const firstTime = times.at(0);
  const lastTime = times.at(-1);
  if (firstTime === undefined || lastTime === undefined) return;

  const values = [
    ...snapshot.bars.flatMap((bar) => [bar.open, bar.high, bar.low, bar.close]),
    ...snapshot.levels.map((level) => level.price),
  ].filter((value) => Number.isFinite(value) && value > 0);
  if (values.length === 0) return;

  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = Math.max(max - min, max * 0.01);
  const pad = range * 0.2;
  const lower = Math.max(0, min - pad);
  const upper = max + pad;

  const addBound = (value: number) => {
    const series = chart.addLineSeries({
      color: "rgba(0, 0, 0, 0)",
      lineWidth: 1,
      priceLineVisible: false,
      lastValueVisible: false,
      crosshairMarkerVisible: false,
    });
    series.setData([
      { time: firstTime, value },
      { time: lastTime, value },
    ]);
  };
  addBound(lower);
  addBound(upper);
}

function toChartTime(value: string): Time {
  if (value.includes("T")) {
    return Math.floor(Date.parse(value) / 1000) as UTCTimestamp;
  }
  return value;
}

function toTimestamp(value: string | undefined): number | null {
  if (!value) return null;
  if (!value.includes("T")) return null;
  const parsed = Math.floor(Date.parse(value) / 1000);
  return Number.isFinite(parsed) ? parsed : null;
}
